
import transformModules from '@/transforms/transformModules';
import transformPages, { TransformedPage } from '@/transforms/transformPages';
import { Context } from '@nuxt/types';
import { ISbStoryData } from '@storyblok/js';
import PortalVue from 'portal-vue';
import { StoryblokResult } from 'storyblok-js-client';
import Vue from 'vue';
import ArticlePage from '~/components/contentTypes/ArticlePage.vue';
import LegalPage from '~/components/contentTypes/LegalPage.vue';
import { ArticleStoryblok, LegalPageStoryblok, PageStoryblok } from '~/types/storyblok-generated';
import { GeigerLocale, getValidLocale } from '~/utility/i18n';
import { generateBreadcrumbsSchema } from '~/utility/jsonld/breadcrumbs';
import { generateFAQSchema } from '~/utility/jsonld/faq';
import { generateOrganizationSchema } from '~/utility/jsonld/organization';
import { generateWebsiteSchema } from '~/utility/jsonld/website';

Vue.use(PortalVue);

const storyblokRelations = ['lm-slider.slides', 'em-map.locations'];

export default Vue.extend({
  transition: {
    name: 'page',
    beforeLeave() {
      document.body.dataset.greenBg = 'true';
      document.body.dataset.isTransitioning = 'true';
    },
    afterEnter() {
      document.body.dataset.greenBg = 'false';
      setTimeout(() => {
        document.body.dataset.isTransitioning = 'false';
      }, 500);
    },
    afterLeave() {
      setTimeout(() => {
        document.body.dataset.serviceLayerOpen = 'false';
      }, 500);
    },
  },

  async asyncData(context: Context): Promise<any> {
    async function getErrorPage(context: Context, locale: any, fullPath: string, error: any) {
      const { data }: StoryblokResult = await context.app.$storyapi.get(`cdn/stories/${locale.code}/error-page`, {
        version: process.env.STORYBLOK_CONTENT_VERSION,
        resolve_links: 'url',
      });
      const modules = await transformModules(data.story.content.modules, context);
      const page = await transformPages(data.story, context);
      const pageError = fullPath !== '/' ? error : null;
      return { modules, page, pageError };
    }
    const path = context.params?.pathMatch;
    const fullPath = context.route.path;
    const locale = getValidLocale(context.route.path.split('/')[1]);
    try {
      let slug = locale.code === path || fullPath.length == 6 ? path : `${locale.code}/${path}`;
      const { data } = await context.app.$storyapi.get(`cdn/stories/${slug}`, {
        version: process.env.STORYBLOK_CONTENT_VERSION,
        resolve_links: 'url',
        resolve_relations: storyblokRelations.join(','),
      });
      if (!data || data.story?.content?.component === 'nav-config') {
        return await getErrorPage(context, locale, fullPath, { status: 404 });
      }
      const modules = await transformModules(data.story.content.modules, context);
      const page = await transformPages(data.story, context);
      const pageError = slug.includes('error') ? { status: 404 } : null;
      return { modules, page, pageError };
    } catch (error: any) {
      try {
        return await getErrorPage(context, locale, fullPath, error);
      } catch (error: any) {
        if (fullPath !== '/') {
          throw new Error(error);
        }
      }
    }
  },

  data() {
    return {
      modules: <any>[],
      page: <TransformedPage>{},
      pageError: null,
    };
  },

  head() {
    const lang = this.$route.path.split('/')[1].split('-')[1];
    const pageMeta = this.page.meta || this.page.Meta;
    if (this.pageError) {
      pageMeta.meta = this.page.meta.meta.map((metaItem: any) => {
        if (metaItem.hid == 'robots') metaItem.content = 'noindex, nofollow';
        return metaItem;
      });
    }
    return {
      htmlAttrs: {
        lang,
      },
      ...pageMeta,
      script: [
        {
          type: 'application/ld+json',
          json: generateWebsiteSchema(this.$i18n.locale as GeigerLocale),
          body: true,
        },
        {
          type: 'application/ld+json',
          json: generateOrganizationSchema(this.$marketSettings, this.$i18n.locale as GeigerLocale),
          body: true,
        },
        {
          type: 'application/ld+json',
          json: generateBreadcrumbsSchema(this.page, this.$i18n.locale as GeigerLocale),
          body: true,
        },
        {
          type: 'application/ld+json',
          json: generateFAQSchema(this.modules),
          body: true,
        },
        {
          type: 'application/javascript',
          src: 'https://app.usercentrics.eu/browser-ui/latest/loader.js',
          id: 'usercentrics-cmp',
          async: true,
          hid: 'usercentrics-cmp',
          'data-settings-id': '31cqfXZz1',
          'data-tcf-enabled': true,
          'data-language': lang,
        },
      ],
      __dangerouslyDisableSanitizers: ['script'],
    };
  },

  mounted() {
    this.$storybridge(() => {
      //@ts-ignore
      const storyblokInstance = new StoryblokBridge({
        resolveRelations: storyblokRelations,
      });
      storyblokInstance.on(['input', 'published', 'change'], async (event: StoryblokEventPayload) => {
        if (event.slugChanged) {
          location.reload();
        }

        if (event.action === 'input') {
          await event.story?.content.modules.filter((item: any) => {
            if (item.component === 'lm-slider' && item.slides.length > 0) {
              const slides = item.slides.reverse();
              return slides;
            }
          });

          if (event.story && event.story.id === this.page.id) {
            this.modules = await transformModules(event.story.content.modules, this.$nuxt.context);
            this.page = await transformPages(
              event.story as ISbStoryData<ArticleStoryblok | PageStoryblok | LegalPageStoryblok>,
              this.$nuxt.context
            );
          }
        }
      });
    });
    this.$updateGlobalData();
  },
});
